import { DEFAULT_LOCALE } from './constants'

/**
 * Minimal country shape needed to resolve locale configuration.
 * Matches a subset of the Country interface from common/types/Country.ts.
 */
export type CountryLocaleInput = {
  iso_3166_2?: string
  currency_code?: string
  currency_symbol?: string
  currency_decimals?: number
}

/** Resolved locale formatting configuration */
export interface LocaleConfig {
  locale: string
  decimalSeparator: string
  groupSeparator: string
  currencySymbol: string
  decimals: number
}

/**
 * ISO 3166-2 country code -> BCP 47 locale string.
 * Extend as needed when new countries are supported.
 */
export const COUNTRY_LOCALE_MAP: Record<string, string> = {
  US: 'en-US',
  CA: 'en-CA',
  GB: 'en-GB',
  AU: 'en-AU',
  NZ: 'en-NZ',
  DE: 'de-DE',
  AT: 'de-AT',
  CH: 'de-CH',
  FR: 'fr-FR',
  ES: 'es-ES',
  IT: 'it-IT',
  PT: 'pt-PT',
  BR: 'pt-BR',
  MX: 'es-MX',
  AR: 'es-AR',
  CL: 'es-CL',
  CO: 'es-CO',
  JP: 'ja-JP',
  KR: 'ko-KR',
  CN: 'zh-CN',
  IN: 'en-IN',
  SE: 'sv-SE',
  NO: 'nb-NO',
  DK: 'da-DK',
  FI: 'fi-FI',
  NL: 'nl-NL',
  BE: 'nl-BE',
  PL: 'pl-PL',
  CZ: 'cs-CZ',
  ZA: 'en-ZA',
  IE: 'en-IE',
}

const DEFAULT_FALLBACK_LOCALE = 'en-CA'

/**
 * Resolve a BCP 47 locale string from country data.
 *
 * Fallback chain:
 * 1. COUNTRY_LOCALE_MAP[iso_3166_2]
 * 2. DEFAULT_LOCALE[currency_code] (from constants.ts)
 * 3. 'en-CA'
 */
export function getLocaleFromCountry(country?: CountryLocaleInput): string {
  if (!country) return DEFAULT_FALLBACK_LOCALE

  const { iso_3166_2, currency_code } = country

  if (iso_3166_2 && COUNTRY_LOCALE_MAP[iso_3166_2]) {
    return COUNTRY_LOCALE_MAP[iso_3166_2]
  }

  if (currency_code && DEFAULT_LOCALE[currency_code.toUpperCase()]) {
    return DEFAULT_LOCALE[currency_code.toUpperCase()]
  }

  return DEFAULT_FALLBACK_LOCALE
}

/**
 * Get full locale formatting configuration for a country.
 * Uses Intl.NumberFormat.formatToParts() to dynamically extract separators
 * rather than hardcoding separator tables.
 *
 * When country is undefined, returns Canada defaults:
 * { locale: 'en-CA', decimalSeparator: '.', groupSeparator: ',', currencySymbol: '$', decimals: 2 }
 */
export function getLocaleConfig(country?: CountryLocaleInput): LocaleConfig {
  const locale = getLocaleFromCountry(country)
  const decimals = country?.currency_decimals ?? 2
  const currencySymbol = country?.currency_symbol || '$'

  // Use Intl.NumberFormat to extract separators for this locale
  const formatter = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })

  const parts = formatter.formatToParts(1234567.89)

  let decimalSeparator = '.'
  let groupSeparator = ','

  for (const part of parts) {
    if (part.type === 'decimal') {
      decimalSeparator = part.value
    } else if (part.type === 'group') {
      groupSeparator = part.value
    }
  }

  return {
    locale,
    decimalSeparator,
    groupSeparator,
    currencySymbol,
    decimals,
  }
}
